import React, { useEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Card, Row, Col, Button, Typography, Space, Tag, Divider, Radio, App, List, Input, Alert, Modal, Spin, Tabs } from 'antd'
import { 
  CreditCardOutlined, 
  GiftOutlined, 
  CrownOutlined,
  StarOutlined,
  CheckCircleOutlined,
  TrophyOutlined,
  RocketOutlined,
  PayCircleOutlined
} from '@ant-design/icons'
import { motion } from 'framer-motion'
import { useAuthStore } from '@/store/authStore'
import { on as onEvent } from '@/services/events'
import { apiClient } from '@/services/api'
import { COMMON_STYLES } from '@/styles/globalStyles'
import { couponService, CouponInfo } from '@/services/couponService'
import '@/styles/global.css'

const { Title, Paragraph, Text } = Typography

const PaymentPage: React.FC = () => {
  const { message } = App.useApp()
  const [loading, setLoading] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  
  // 检测是否为移动端
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])
  
  const [selectedPackage, setSelectedPackage] = useState<string>('')
  const [selectedPlan, setSelectedPlan] = useState<string>('')
  const [paymentMethod, setPaymentMethod] = useState<string>('wechat')
  const [activeTab, setActiveTab] = useState<'credits'>('credits')
  const [userTouchedSelection, setUserTouchedSelection] = useState<boolean>(false)
  const [couponCode, setCouponCode] = useState('')
  const [couponInfo, setCouponInfo] = useState<CouponInfo | null>(null)
  const [verifyingCoupon, setVerifyingCoupon] = useState(false)
  const [usingCoupon, setUsingCoupon] = useState(false)
  // 订阅用户对象，确保积分变化实时渲染
  const user = useAuthStore((s) => s.user)
  // 订阅公共配置，获取支付网关设置（已移除未使用的 configs）
  
  
  // 支付历史和订阅状态
  const [paymentHistory, setPaymentHistory] = useState<any[]>([])
  const [subscription, setSubscription] = useState<any>(null)
  const [historyLoading, setHistoryLoading] = useState(false)
  
  // 二维码弹窗状态（用于聚合支付PC端）
  const [qrCodeVisible, setQrCodeVisible] = useState(false)
  const [qrCodeUrl, setQrCodeUrl] = useState('')
  const [currentOrderId, setCurrentOrderId] = useState('')
  const [polling, setPolling] = useState(false)
  const pollingIntervalRef = useRef<number | null>(null)
  
  // 实时同步：支付完成后更新余额与订单列表
  useEffect(() => {
    const handleCredits = (p: any) => {
      if (!user || p?.userId !== user.id) return
      // 静默刷新支付历史，无需弹出提示
      fetchPaymentHistory()
    }
    const handlePayment = (p: any) => {
      if (!user) return
      // 增量更新支付历史
      setPaymentHistory(prev => {
        const idx = prev.findIndex(item => item.id === p.id)
        if (idx >= 0) {
          const next = [...prev]
          next[idx] = { ...next[idx], ...p }
          return next
        }
        return [p, ...prev]
      })
    }
    onEvent('credits_updated', handleCredits)
    onEvent('payment_updated', handlePayment)
  }, [user])

  // 页面加载时检测是否有待支付的订单（H5支付返回后）
  useEffect(() => {
    const checkPendingPayment = async () => {
      const pendingOrderId = localStorage.getItem('pendingPaymentOrderId')
      const pendingTime = localStorage.getItem('pendingPaymentTime')
      
      if (pendingOrderId && pendingTime) {
        const timeDiff = Date.now() - parseInt(pendingTime, 10)
        // 如果订单在5分钟内，开始轮询
        if (timeDiff < 5 * 60 * 1000) {
          console.log('🔍 [PaymentPage] 检测到待支付订单，开始轮询:', pendingOrderId)
          startPollingPaymentStatus(pendingOrderId)
          message.info('检测到未完成的支付，正在检查支付状态...')
        } else {
          // 超过5分钟，清除localStorage
          console.log('⏰ [PaymentPage] 待支付订单已过期，清除记录')
          localStorage.removeItem('pendingPaymentOrderId')
          localStorage.removeItem('pendingPaymentTime')
        }
      }
    }
    
    checkPendingPayment()
  }, [])

  const [packages, setPackages] = useState<any[]>([])
  const [subscriptionPlans, setSubscriptionPlans] = useState<any[]>([])
  const [methods, setMethods] = useState<any[]>([])
  
  // 加载支付配置数据
  const loadPaymentData = async () => {
    try {
      // 获取积分包数据 - 使用公共API
      let finalPackages: any[] = []
      const packagesRes = await apiClient.get('/payment/credit-packages')
      if (packagesRes.success && packagesRes.data?.packages) {
        const enabledPackages = packagesRes.data.packages.filter((pkg: any) => pkg.enabled)
        setPackages(enabledPackages)
        finalPackages = enabledPackages
      } else {
        finalPackages = defaultCreditPackages
      }
      
      // 获取订阅计划数据 - 使用公共API
      let finalPlans: any[] = []
      const plansRes = await apiClient.get('/payment/subscription-plans')
      if (plansRes.success && plansRes.data?.plans) {
        const enabledPlans = plansRes.data.plans.filter((plan: any) => plan.enabled)
        setSubscriptionPlans(enabledPlans)
        finalPlans = enabledPlans
      } else {
        finalPlans = [
          {
            id: '1',
            name: '基础会员',
            price: 19.99,
            period: 'monthly',
            credits: 200,
            features: ['每月200积分', '优先支持', '无广告'],
            color: 'blue',
            popular: false,
            isDefault: false
          },
          {
            id: '2',
            name: 'Pro会员',
            price: 49.99,
            period: 'monthly',
            credits: 600,
            features: ['每月600积分', '优先支持', '无广告', '高级功能'],
            color: 'purple',
            popular: true,
            isDefault: true
          },
          {
            id: '3',
            name: '年度会员',
            price: 499.99,
            period: 'yearly',
            credits: 8000,
            features: ['全年8000积分', 'VIP支持', '无广告', '全部功能', '专属客服'],
            color: 'gold',
            popular: false,
            isDefault: false
          }
        ]
      }

      // 获取支付方式数据（兼容两种返回结构）
      const methodsRes = await apiClient.get('/payment/methods')
      if (methodsRes?.success) {
        const raw = (methodsRes.data?.methods ?? methodsRes.data ?? []) as any[]
        const enabledMethods = raw.filter((m: any) => m?.enabled !== false)
        setMethods(enabledMethods)
        const def = enabledMethods.find((m: any) => m.isDefault) || enabledMethods[0]
        if (def?.id) setPaymentMethod(def.id)
      } else {
        setMethods([])
      }

      // 默认选中推荐项的逻辑
      // 查找推荐的积分包（优先级：isDefault > popular > 第一项）
      const recommendedPackage = finalPackages.find(pkg => pkg.isDefault) || 
                                 finalPackages.find(pkg => pkg.popular) || 
                                 finalPackages[0]
      
      // 查找推荐的订阅计划（优先级：isDefault > popular > 第一项）
      const recommendedPlan = finalPlans.find(plan => plan.isDefault) || 
                             finalPlans.find(plan => plan.popular) || 
                             finalPlans[0]
      
      // 决定默认显示哪个 Tab 和选中哪个项（优先积分包）
      if (recommendedPackage) {
        setActiveTab('credits')
        setSelectedPackage(recommendedPackage.id)
        setSelectedPlan('')
        console.log('✅ 默认选中（优先积分包）:', recommendedPackage.name)
      } else if (recommendedPlan) {
        // 订阅套餐标签已移除，默认使用积分包标签
        setActiveTab('credits')
        setSelectedPlan('')
        setSelectedPackage('')
        console.log('✅ 订阅套餐标签已移除，默认显示积分包')
      }
    } catch (error) {
      console.warn('获取支付配置失败，使用默认数据', error)
    }
  }

  useEffect(() => {
    loadPaymentData()
  }, [])

  // 监听配置更新事件
  useEffect(() => {
    const handleConfigUpdate = () => {
      console.log('🔄 检测到支付配置更新，重新加载数据')
      loadPaymentData()
    }
    const off = onEvent('payment_config_updated', handleConfigUpdate) as unknown as (() => void) | undefined
    return () => {
      try { typeof off === 'function' && off() } catch {}
    }
  }, [])
  
  // 获取支付历史
  const fetchPaymentHistory = async () => {
    try {
      setHistoryLoading(true)
      const res = await apiClient.get('/payment/history?page=1&limit=10')
      if (res.success) {
        setPaymentHistory(res.data?.payments || [])
      }
    } catch (e) {
      console.error('获取支付历史失败', e)
    } finally {
      setHistoryLoading(false)
    }
  }
  
  // 获取订阅状态
  const fetchSubscription = async () => {
    try {
      const res = await apiClient.get('/payment/subscription')
      if (res.success) {
        setSubscription(res.data?.subscription || null)
      }
    } catch (e) {
      console.error('获取订阅状态失败', e)
    }
  }
  
  // 初始加载
  useEffect(() => {
    fetchPaymentHistory()
    fetchSubscription()
  }, [])

  // 默认积分包数据
  const defaultCreditPackages = [
    {
      id: '1',
      name: '入门包',
      credits: 100,
      price: 9.99,
      bonus: 0,
      description: '适合轻度用户',
      popular: false,
      icon: <CreditCardOutlined style={{ fontSize: 'var(--font-h4-mobile)' }} />
    },
    {
      id: '2',
      name: '标准包',
      credits: 500,
      price: 39.99,
      bonus: 50,
      description: '最受欢迎的选择',
      popular: true,
      icon: <StarOutlined className="text-2xl" />
    },
    {
      id: '3',
      name: '专业包',
      credits: 1000,
      price: 69.99,
      bonus: 150,
      description: '最佳性价比',
      popular: false,
      icon: <CrownOutlined style={{ fontSize: 'var(--font-h4-mobile)' }} />
    },
    {
      id: '4',
      name: '旗舰包',
      credits: 3000,
      price: 179.99,
      bonus: 500,
      description: '适合重度用户',
      popular: false,
      icon: <TrophyOutlined className="text-2xl" />
    }
  ]

  const creditPackages = packages.length > 0 ? packages : defaultCreditPackages

  // 新增：默认订阅计划数据（用于名称回退）
  const defaultSubscriptionPlans = [
    { id: '1', name: '基础会员', price: 19.99, period: 'monthly', credits: 200, features: [], color: 'blue', popular: false, isDefault: false },
    { id: '2', name: 'Pro会员', price: 49.99, period: 'monthly', credits: 600, features: [], color: 'purple', popular: true, isDefault: true },
    { id: '3', name: '年度会员', price: 499.99, period: 'yearly', credits: 8000, features: [], color: 'gold', popular: false, isDefault: false },
  ]

  // 推荐项获取（isDefault > popular > 第一项）
  const getRecommendedPackage = () => {
    const list = (packages.length > 0 ? packages : defaultCreditPackages) as any[]
    return list.find(p => p.isDefault) || list.find(p => p.popular) || list[0]
  }
  const getRecommendedPlan = () => {
    const list = (subscriptionPlans.length > 0 ? subscriptionPlans : defaultSubscriptionPlans) as any[]
    return list.find(p => p.isDefault) || list.find(p => p.popular) || list[0]
  }

  // 验证优惠券
  const handleVerifyCoupon = async () => {
    if (!couponCode.trim()) {
      message.warning('请输入优惠券代码')
      return
    }

    setVerifyingCoupon(true)
    try {
      const info = await couponService.verifyCoupon(couponCode.trim())
      setCouponInfo(info)
      message.success(`优惠券有效！将获得 ${info.credits} 积分`)
    } catch (error: any) {
      setCouponInfo(null)
      message.error(error.response?.data?.message || error.message || '优惠券验证失败')
    } finally {
      setVerifyingCoupon(false)
    }
  }

  // 使用优惠券充值
  const handleUseCoupon = async () => {
    if (!couponCode.trim()) {
      message.warning('请输入优惠券代码')
      return
    }

    if (!couponInfo) {
      message.warning('请先验证优惠券')
      return
    }

    setUsingCoupon(true)
    try {
      const result = await couponService.useCoupon(couponCode.trim())
      message.success(`优惠券使用成功！获得 ${result.credits} 积分，当前余额：${result.newBalance}`)
      setCouponCode('')
      setCouponInfo(null)
      // 刷新用户信息
      const { refreshToken } = useAuthStore.getState()
      await refreshToken()
      // 刷新支付历史
      fetchPaymentHistory()
    } catch (error: any) {
      message.error(error.response?.data?.message || error.message || '优惠券使用失败')
    } finally {
      setUsingCoupon(false)
    }
  }

  // 支付状态轮询（用于PC端Native支付和移动端H5支付）
  const startPollingPaymentStatus = (orderId: string) => {
    // 清理之前的轮询
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
    }
    
    setPolling(true)
    setCurrentOrderId(orderId) // 保存订单ID，用于手动检查
    let retryCount = 0
    const maxRetries = 3
    let pollCount = 0
    const maxPollCount = 30 // 最多轮询30次（60秒）
    
    pollingIntervalRef.current = setInterval(async () => {
      try {
        pollCount++
        console.log(`🔍 [PaymentPage] 轮询支付状态 (${pollCount}/${maxPollCount}):`, orderId)
        
        const res = await apiClient.get(`/payment/status/${orderId}`)
        if (res.success && res.data?.status === 'COMPLETED') {
          // 支付成功
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current)
            pollingIntervalRef.current = null
          }
          setPolling(false)
          setQrCodeVisible(false)
          setCurrentOrderId('')
          
          // 清除localStorage中的待支付订单
          localStorage.removeItem('pendingPaymentOrderId')
          localStorage.removeItem('pendingPaymentTime')
          
          message.success('支付成功！')
          
          // 🔥 修复：立即刷新用户信息（包括积分），然后刷新支付历史
          const { refreshUser } = useAuthStore.getState()
          try {
            await refreshUser()
            console.log('✅ [PaymentPage] 用户信息已刷新，积分已更新')
          } catch (error) {
            console.error('❌ [PaymentPage] 刷新用户信息失败:', error)
          }
          
          // 延迟刷新支付历史，确保后端处理完成
          setTimeout(() => {
            fetchPaymentHistory()
            // 再次刷新用户信息，确保积分同步
            refreshUser().catch(err => {
              console.warn('⚠️ [PaymentPage] 二次刷新用户信息失败:', err)
            })
          }, 500)
        } else if (res.success && res.data?.status === 'FAILED') {
          // 支付失败
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current)
            pollingIntervalRef.current = null
          }
          setPolling(false)
          setCurrentOrderId('')
          localStorage.removeItem('pendingPaymentOrderId')
          localStorage.removeItem('pendingPaymentTime')
          message.error('支付失败，请重试')
        }
        retryCount = 0 // 重置重试计数
      } catch (error) {
        console.error('轮询支付状态失败:', error)
        retryCount++
        if (retryCount >= maxRetries) {
          console.warn(`轮询失败达到最大重试次数 ${maxRetries}，暂停一次轮询`)
          retryCount = 0
        }
      }
      
      // 达到最大轮询次数，停止轮询
      if (pollCount >= maxPollCount) {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current)
          pollingIntervalRef.current = null
        }
        setPolling(false)
        message.info('自动检查已停止，您可以手动检查支付状态')
      }
    }, 2000) // 每2秒轮询一次
  }

  // 手动检查支付状态
  const manualCheckPaymentStatus = async (orderId: string) => {
    try {
      setPolling(true)
      const res = await apiClient.get(`/payment/status/${orderId}`)
      if (res.success && res.data?.status === 'COMPLETED') {
        // 支付成功
        setPolling(false)
        setQrCodeVisible(false)
        message.success('支付成功！')
        
        // 刷新用户信息和支付历史
        const { refreshToken } = useAuthStore.getState()
        await refreshToken()
        setTimeout(() => fetchPaymentHistory(), 500)
      } else {
        setPolling(false)
        message.info('支付尚未完成，请继续等待或重新扫码')
      }
    } catch (error) {
      setPolling(false)
      console.error('手动检查支付状态失败:', error)
      message.error('检查支付状态失败，请稍后重试')
    }
  }
  
  // 清理轮询
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
      }
    }
  }, [])

  // 处理支付URL，根据协议类型选择打开方式
  const handlePaymentUrl = (paymentData: any) => {
    console.log('🔍 [PaymentPage] handlePaymentUrl 调用:', {
      paymentMethod,
      paymentData,
      hasQrCode: !!paymentData.qrCode,
      hasPaymentUrl: !!paymentData.paymentUrl
    })
    
    // 聚合支付特殊处理（PC端显示二维码，移动端跳转）
    if (paymentMethod === 'xunhu') {
      const isMobile = /Mobile|Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
      const deviceType = paymentData.deviceType || (isMobile ? 'mobile' : 'pc')
      
      console.log('🔍 [PaymentPage] 聚合支付检测:', {
        isMobile,
        deviceType,
        qrCode: paymentData.qrCode,
        paymentUrl: paymentData.paymentUrl
      })
      
      if (deviceType === 'pc' && paymentData.qrCode) {
        // PC端显示二维码
        console.log('✅ [PaymentPage] PC端显示二维码:', paymentData.qrCode)
        setQrCodeUrl(paymentData.qrCode)
        setCurrentOrderId(paymentData.orderId)
        setQrCodeVisible(true)
        startPollingPaymentStatus(paymentData.orderId)
      } else {
        // 移动端跳转支付URL（优化：在新窗口打开并开始轮询）
        const url = paymentData.paymentUrl || paymentData.payUrl
        console.log('📱 [PaymentPage] 移动端聚合支付跳转:', url)
        console.log('📱 [PaymentPage] 订单ID:', paymentData.orderId)
        
        if (url) {
          // 保存订单ID到localStorage，用于支付返回后检测
          if (paymentData.orderId) {
            localStorage.setItem('pendingPaymentOrderId', paymentData.orderId)
            localStorage.setItem('pendingPaymentTime', Date.now().toString())
            console.log('💾 [PaymentPage] 已保存订单ID到localStorage:', paymentData.orderId)
            
            // 开始轮询支付状态
            console.log('🔄 [PaymentPage] 开始轮询聚合支付状态...')
            startPollingPaymentStatus(paymentData.orderId)
          }
          
          // 在新窗口打开支付页面（这样用户支付完成后可以返回原页面）
          const paymentWindow = window.open(url, '_blank')
          
          // 如果新窗口被阻止，则直接跳转
          if (!paymentWindow) {
            console.warn('⚠️ [PaymentPage] 无法打开新窗口，直接跳转')
            window.location.href = url
          }
        }
      }
      return
    }
    
    // 处理JSAPI支付（微信内支付）
    if (paymentData.jsapiParams) {
      // 需要在微信环境中调起微信支付SDK
      const invokePay = () => {
        if (typeof window !== 'undefined' && (window as any).WeixinJSBridge) {
          (window as any).WeixinJSBridge.invoke(
            'getBrandWCPayRequest',
            paymentData.jsapiParams,
            (res: any) => {
              if (res.err_msg === 'get_brand_wcpay_request:ok') {
                message.success('支付成功')
                // 刷新支付历史
                setTimeout(() => fetchPaymentHistory(), 1000)
              } else if (res.err_msg === 'get_brand_wcpay_request:cancel') {
                message.info('支付已取消')
              } else {
                message.error('支付失败：' + res.err_msg)
              }
            }
          )
        } else {
          message.warning('请在微信内打开页面完成支付')
        }
      }

      // 如果WeixinJSBridge已加载，直接调用；否则等待加载
      if (typeof window !== 'undefined' && (window as any).WeixinJSBridge) {
        invokePay()
      } else if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        // 等待WeixinJSBridge加载
        document.addEventListener('WeixinJSBridgeReady', invokePay, false)
        // 如果等待2秒后仍未加载，提示用户
        setTimeout(() => {
          if (!(window as any).WeixinJSBridge) {
            message.warning('请在微信内打开页面完成支付')
          }
        }, 2000)
      } else {
        message.warning('请在微信内打开页面完成支付')
      }
      return
    }

    // 处理H5支付（移动浏览器）
    if (paymentData.h5Url) {
      console.log('📱 [PaymentPage] H5支付跳转:', paymentData.h5Url)
      console.log('📱 [PaymentPage] 订单ID:', paymentData.orderId)
      
      // 保存订单ID到localStorage，用于支付返回后检测
      if (paymentData.orderId) {
        localStorage.setItem('pendingPaymentOrderId', paymentData.orderId)
        localStorage.setItem('pendingPaymentTime', Date.now().toString())
        console.log('💾 [PaymentPage] 已保存订单ID到localStorage:', paymentData.orderId)
        
        // 开始轮询支付状态（参考PC端实现）
        console.log('🔄 [PaymentPage] 开始轮询H5支付状态...')
        startPollingPaymentStatus(paymentData.orderId)
      }
      
      // 在新窗口打开微信支付（这样用户支付完成后可以返回原页面）
      const paymentWindow = window.open(paymentData.h5Url, '_blank')
      
      // 如果新窗口被阻止，则直接跳转
      if (!paymentWindow) {
        console.warn('⚠️ [PaymentPage] 无法打开新窗口，直接跳转')
        window.location.href = paymentData.h5Url
      }
      
      return
    }

    // 处理Native支付（扫码支付）或通用支付URL
    const url = paymentData.paymentUrl || paymentData.payUrl
    if (url) {
      // 检测设备类型
      const isMobile = /Mobile|Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
      
      // 判断URL协议类型
      if (url.startsWith('weixin://')) {
        if (!isMobile && paymentMethod === 'wechat') {
          // PC端微信支付：显示二维码
          console.log('✅ [PaymentPage] PC端微信支付显示二维码:', url)
          
          // 使用在线QR码生成服务
          const qrCodeImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=256x256&data=${encodeURIComponent(url)}`
          setQrCodeUrl(qrCodeImageUrl)
          setCurrentOrderId(paymentData.orderId)
          setQrCodeVisible(true)
          startPollingPaymentStatus(paymentData.orderId)
        } else {
          // 移动端：直接跳转打开微信
          window.location.href = url
        }
      } else if (url.startsWith('http://') || url.startsWith('https://')) {
        // HTTP/HTTPS链接，在新窗口打开
        window.open(url, '_blank')
      } else {
        // 其他协议，尝试直接跳转
        window.location.href = url
      }
    }
  }

  // 处理积分包选择
  const handlePackageSelect = (packageId: string) => {
    // 如果当前不在积分包 tab，不需要切换（由用户手动切换 tab）
    if (activeTab !== 'credits') {
      return
    }
    setSelectedPackage(packageId)
    setSelectedPlan('') // 取消选择订阅计划
  }

  // 处理订阅计划选择（订阅套餐标签已移除，此函数不再使用）
  const handlePlanSelect = (planId: string) => {
    // 订阅套餐标签已移除，此函数保留以避免编译错误，但不执行任何操作
    console.log('⚠️ 订阅套餐标签已移除，handlePlanSelect 不再使用')
  }

  const handlePurchaseCredits = async () => {
    if (!selectedPackage) {
      message.warning('请选择积分包')
      return
    }
    setLoading(true)
    const allPackages = packages.length > 0 ? packages : creditPackages;
    const pkg = allPackages.find(p => p.id === selectedPackage)
    const isUsingDefaultPackage = packages.length === 0
    
    try {
      // 积分包购买：不传 planId，直接传 amount 和 credits
      // 因为积分包可能来自配置（payment_packages），不在 Plan 表中
      // 只有订阅计划（SUBSCRIPTION）才需要传 planId
      const requestBody: any = {
        amount: Number((pkg as any)?.price) || 0,
        credits: Number((pkg as any)?.credits) || 0,
        paymentMethod,
        type: 'CREDITS',
        scene: 'normal',
        couponCode: couponCode.trim() || undefined
        // 注意：积分包不传 planId，避免后端在 Plan 表中查找失败
      }
      
      // 使用API文档中的正确端点
      const res = await apiClient.post('/payment/create-order', requestBody)
      
      // 如果使用优惠券成功（返回isCoupon标识），直接返回
      if (res.success && res.data?.isCoupon) {
        message.success(`优惠券使用成功！获得 ${res.data.credits} 积分`)
        setCouponCode('')
        setCouponInfo(null)
        // 🔥 修复：立即刷新用户信息（包括积分）
        const { refreshUser } = useAuthStore.getState()
        try {
          await refreshUser()
          console.log('✅ [PaymentPage] 优惠券使用成功，用户信息已刷新')
        } catch (error) {
          console.error('❌ [PaymentPage] 刷新用户信息失败:', error)
        }
        setTimeout(() => {
          fetchPaymentHistory()
          // 再次刷新用户信息，确保积分同步
          refreshUser().catch(err => {
            console.warn('⚠️ [PaymentPage] 二次刷新用户信息失败:', err)
          })
        }, 500)
        return
      }
      if (res.success) {
        console.log('✅ [PaymentPage] 订单创建成功:', res.data)
        message.success('支付订单已创建，请完成支付')
        // 使用新的支付URL处理函数
        handlePaymentUrl(res.data)
        // 刷新支付历史
        setTimeout(() => fetchPaymentHistory(), 500)
      } else {
        console.error('❌ [PaymentPage] 订单创建失败:', res.message)
        throw new Error(res.message || '下单失败')
      }
    } catch (e: any) {
      message.error(e?.message || '下单失败')
    } finally {
      setLoading(false)
    }
  }

  const handleSubscribe = async () => {
    if (!selectedPlan) {
      message.warning('请选择订阅计划')
      return
    }
    setLoading(true)
    const allPlans = subscriptionPlans.length > 0 ? subscriptionPlans : [
      {
        id: '1',
        name: '基础会员',
        price: 19.99,
        period: 'monthly',
        credits: 200,
        features: ['每月200积分', '优先支持', '无广告'],
        color: 'blue',
        popular: false
      },
      {
        id: '2',
        name: 'Pro会员',
        price: 49.99,
        period: 'monthly',
        credits: 600,
        features: ['每月600积分', '优先支持', '无广告', '高级功能'],
        color: 'purple',
        popular: true
      },
      {
        id: '3',
        name: '年度会员',
        price: 499.99,
        period: 'yearly',
        credits: 8000,
        features: ['全年8000积分', 'VIP支持', '无广告', '全部功能', '专属客服'],
        color: 'gold',
        popular: false
      }
    ];
    const plan = allPlans.find(p => p.id === selectedPlan);
    const isUsingDefaultPlan = subscriptionPlans.length === 0
    
    try {
      // 订阅套餐购买：不传 planId，直接传 amount 和 credits
      // 因为订阅套餐可能来自配置（payment_plans），不在 Plan 表中
      // 只有数据库中的 Plan 记录才需要传 planId
      const requestBody: any = {
        amount: Number((plan as any)?.price) || 0,
        credits: Number((plan as any)?.credits) || 0,
        paymentMethod,
        type: 'SUBSCRIPTION',
        scene: 'normal'
        // 注意：订阅套餐不传 planId，避免后端在 Plan 表中查找失败
      }
      
      // 使用API文档中的正确端点
      const res = await apiClient.post('/payment/create-order', requestBody)
      if (res.success) {
        message.success('订阅订单已创建，请完成支付')
        // 使用新的支付URL处理函数
        handlePaymentUrl(res.data)
        // 刷新订阅状态和支付历史
        setTimeout(() => {
          fetchSubscription()
          fetchPaymentHistory()
        }, 500)
      } else {
        throw new Error(res.message || '订阅失败')
      }
    } catch (e: any) {
      message.error(e?.message || '订阅失败')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="payment-page min-h-screen w-full relative">
      {/* 移动端背景装饰 */}
      <div className="md:hidden absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-gray-100/50 to-transparent pointer-events-none page-mobile-hero"></div>
      <div className="unified-container page-with-fixed-button relative z-10" style={{
        paddingTop: 'var(--spacing-sm)'
      }}>
        <motion.div
          {...COMMON_STYLES.pageEnter}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="flex flex-col"
        >
          {/* 头部设计 - 左右结构 */}
          <div className="w-full page-header-section">
            <motion.div
              {...COMMON_STYLES.pageEnter}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex flex-col md:flex-row md:items-center md:justify-between"
              style={{
                marginBottom: 'var(--spacing-lg)',
                gap: 'var(--spacing-md)'
              }}
            >
            {/* 左侧：标题和副标题 */}
            <div className="flex-1">
              <div className="flex items-center" style={{
                gap: 'var(--spacing-md)',
                marginBottom: 'var(--spacing-sm)'
              }}>
                <span style={{ fontSize: 'var(--font-h1-mobile)' }}>💎</span>
                <h1 className="unified-title-h1 mb-0 bg-gradient-to-r from-yellow-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  积分充值中心
                </h1>
              </div>
              <Paragraph className="unified-text-secondary" style={{
                fontSize: 'var(--font-body)',
                marginLeft: 'var(--spacing-xxl)',
                marginBottom: 0
              }}>
                选择适合你的积分包，解锁更多AI创作可能
              </Paragraph>
            </div>
            
            {/* 右侧：特色标签 */}
            <div className="flex flex-wrap md:justify-end" style={{ gap: 'var(--spacing-sm)' }}>
              <Tag icon={<GiftOutlined />} className="unified-tag bg-gradient-to-r from-yellow-50 to-yellow-100 text-yellow-700">
                {user?.pointsBalance || 0} 积分
              </Tag>
              <Tag icon={<RocketOutlined />} className="unified-tag bg-gradient-to-r from-purple-50 to-purple-100 text-purple-700">
                多种套餐
              </Tag>
              <Tag icon={<StarOutlined />} className="unified-tag bg-gradient-to-r from-pink-50 to-pink-100 text-pink-700">
                超值优惠
              </Tag>
            </div>
          </motion.div>
          </div>


          <Row gutter={[0, 16]}>
            <Col xs={24}>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <Card 
                  title="💎 充值中心" 
                  className="unified-card"
                  style={{ marginBottom: 'var(--spacing-xl)' }}
                  styles={{ body: { minHeight: 180 } }}
                >
                  {/* 标签页切换：积分包、订阅套餐 */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 'var(--spacing-md)' }}>
                  <Tabs 
                      className="profile-payment-tabs"
                    size="large"
                    animated
                      centered={false}
                    activeKey={activeTab}
                    onChange={(key) => {
                      const k = key as 'credits'
                      console.log('🔄 [PaymentPage] 切换标签:', k)
                      setActiveTab(k)
                      
                      // 切换 Tab 时，自动选中当前 Tab 的推荐项，确保支付按钮同步更新
                      if (k === 'credits') {
                        // 获取推荐积分包
                          const rec = getRecommendedPackage()
                          if (rec?.id) {
                          // 同时更新多个状态，React 会自动批处理
                            setSelectedPackage(rec.id)
                          setSelectedPlan('') // 清空订阅套餐选择
                          console.log('✅ [PaymentPage] 切换到积分包，自动选中:', rec.name, 'ID:', rec.id, '价格:', rec.price)
                        } else {
                          console.warn('⚠️ [PaymentPage] 切换到积分包，未找到推荐项')
                          setSelectedPackage('')
                            setSelectedPlan('')
                          }
                      }
                    }}
                    items={[
                      {
                        key: 'credits',
                        label: (
                          <span className="flex items-center" style={{ gap: 'var(--spacing-sm)' }}>
                            <span>💎</span>
                            <span className="font-medium">积分包</span>
                          </span>
                        ),
                        children: (
                          <div>
                            {/* 提示文字 - 在 Tabs 横线下方 */}
                            <div className="text-center" style={{ 
                              marginTop: 'var(--spacing-md)',
                              marginBottom: 'var(--spacing-md)',
                              paddingTop: '0px',
                              paddingBottom: '0px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              lineHeight: '1'
                            }}>
                              <Text className="text-gray-600" style={{ lineHeight: '1.4', fontSize: '14px', margin: 0, padding: 0 }}>
                                购买多 福利多 体验更好
                              </Text>
                            </div>
                            
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 'var(--spacing-lg)'
                          }}>
                            {(packages.length > 0 ? packages : defaultCreditPackages).map((pkg) => (
                              <div
                                key={pkg.id}
                                  className={`border rounded-lg cursor-pointer transition-all ${
                                    selectedPackage === pkg.id
                                      ? 'border-purple-400 bg-purple-50' 
                                      : 'border-gray-200 hover:border-gray-300'
                                  }`}
                                style={{
                                    padding: 'var(--spacing-md)'
                                }}
                                onMouseEnter={(e) => {
                                  if (selectedPackage !== pkg.id) {
                                    e.currentTarget.style.borderColor = '#d1d5db'
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (selectedPackage !== pkg.id) {
                                    e.currentTarget.style.borderColor = '#e5e7eb'
                                  }
                                }}
                                onClick={() => handlePackageSelect(pkg.id)}
                              >
                          <div className="flex justify-between items-center">
                            <div className="flex-1">
                                    <div className="flex items-center" style={{ gap: 'var(--spacing-sm)' }}>
                                      <div className="font-semibold">{pkg.name}</div>
                                      {pkg.isDefault && (
                                        <Tag color="blue">推荐</Tag>
                                      )}
                                      {pkg.popular && !pkg.isDefault && (
                                        <Tag color="red">热门</Tag>
                                      )}
                              </div>
                                    <div className="text-gray-500" style={{
                                      fontSize: 'var(--font-small)',
                                      marginTop: 'var(--spacing-xs)'
                                    }}>
                                      {pkg.credits}积分
                                      {pkg.bonus > 0 && ` + ${pkg.bonus}赠送`}
                                    </div>
                                    {pkg.description && (
                                      <div className="text-gray-400" style={{
                                        fontSize: 'var(--font-small)',
                                        marginTop: 'var(--spacing-xs)'
                                      }}>
                                {pkg.description}
                          </div>
                                    )}
                            </div>
                                  <div className="text-right" style={{ marginLeft: 'var(--spacing-lg)' }}>
                                    <div className="font-bold text-purple-600" style={{
                                      fontSize: 'var(--font-body)'
                                    }}>
                                      ￥{pkg.price}
                                    </div>
                              {pkg.bonus > 0 && (
                                      <div className="text-green-600 font-semibold" style={{
                                        fontSize: 'var(--font-small)',
                                        marginTop: 'var(--spacing-xs)'
                                      }}>
                                        +{pkg.bonus}赠送
                                  </div>
                              )}
                            </div>
                          </div>
                        </div>
                    ))}
                  </div>
                            </div>
                        )
                      }
                    ]}
                  />
                    </div>
                </Card>
              </motion.div>
            </Col>
          </Row>

          {/* 支付方式 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            style={{ marginTop: 'var(--spacing-sm)' }}
          >
            <Card 
              className="payment-method-card-no-border"
                        style={{
                          borderRadius: 'var(--radius-lg)',
                border: 'none', 
                boxShadow: 'none',
                background: 'transparent'
              }}
              styles={{ 
                body: { 
                  border: 'none', 
                  padding: 0,
                  background: 'transparent'
                }
              }}
            >
              <div>
                <Title level={5} className="unified-title-h5" style={{ marginBottom: 'var(--spacing-sm)' }}>💳 支付方式</Title>
                <Radio.Group 
                  value={paymentMethod}
                  onChange={(e) => {
                    console.log('💳 [PaymentPage] 选择支付方式:', e.target.value)
                    setPaymentMethod(e.target.value)
                  }}
                  style={{ width: '100%' }}
                      >
                  <div className="flex flex-wrap" style={{ gap: 'var(--spacing-lg)' }}>
                    {methods.map((m: any) => {
                      const displayName = m?.id === 'xunhu' ? '微信聚合支付' : (m?.name || m?.id)
                      return (
                        <Radio 
                          key={m.id} 
                          value={m.id}
                          style={{
                            fontSize: '14px',
                            lineHeight: '1.5',
                            margin: 0,
                            whiteSpace: 'nowrap'
                          }}
                        >
                          <span className="font-medium">{displayName}</span>
                        </Radio>
                    )
                  })}
                </div>
                </Radio.Group>
                    </div>
            </Card>
          </motion.div>

          {/* 统一支付按钮 - 桌面端显示 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            style={{ marginTop: 'var(--spacing-sm)' }}
            className="hidden md:block"
          >
            <Card className="border border-gray-100" style={{ borderRadius: 'var(--radius-lg)' }}
              styles={{ body: { padding: 'var(--spacing-sm) var(--spacing-md)' } }}
            >
              <div>
                <Title level={4} className="unified-title-h4" style={{ marginBottom: 'var(--spacing-sm)', marginTop: 0 }}>
                  确认支付
                  <span className="align-middle text-gray-600 font-normal" style={{
                    marginLeft: 'var(--spacing-md)',
                    fontSize: 'var(--font-small)'
                  }}>
                      {selectedPackage
                      ? `💳 积分包: ${((packages.length > 0 ? packages : defaultCreditPackages) as any[]).find((p: any) => p.id === selectedPackage)?.name || ''}`
                      : ''}
                  </span>
                </Title>

                  <Button
                    type="primary"
                    size="large"
                    block
                  onClick={handlePurchaseCredits}
                  disabled={!selectedPackage}
                  loading={loading}
                  className="h-14 font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
                    style={{
                    borderRadius: '12px',
                    background: selectedPackage
                      ? 'linear-gradient(135deg, #a855f7 0%, #8b5cf6 50%, #7c3aed 100%)'
                      : 'linear-gradient(135deg, #d1d5db 0%, #9ca3af 100%)',
                    border: 'none',
                    boxShadow: selectedPackage
                      ? '0 4px 16px rgba(139, 92, 246, 0.4), 0 2px 8px rgba(139, 92, 246, 0.3)'
                      : 'none'
                    }}
                  >
                  {selectedPackage
                    ? (() => {
                        const list: any[] = (packages.length > 0 ? packages : defaultCreditPackages) as any[]
                        const price = list.find(p => p.id === selectedPackage)?.price
                        return (
                          <span>
                            💳 立即购买积分 <span style={{ fontSize: '1.5rem', fontWeight: 700 }}>￥{price ?? ''}</span>
                          </span>
                        )
                      })()
                    : '请选择积分包'}
                  </Button>
              </div>
                </Card>
              </motion.div>

          {/* 安全支付保障：按需求已移除以精简页面 */}
                  </motion.div>
                    </div>

      {/* 移动端固定悬浮按钮 - 使用 Portal 渲染到 body，确保 fixed 定位相对于视口 */}
      {typeof window !== 'undefined' && isMobile && document.body && createPortal(
        <div className="mobile-fixed-button-container">
          <Button
            type="primary"
            size="large"
            block
            onClick={selectedPackage ? handlePurchaseCredits : handleSubscribe}
            disabled={!selectedPackage && !selectedPlan}
            loading={loading}
            className="h-14 font-semibold shadow-lg transition-all duration-200 mobile-pay-button"
            style={{
              borderRadius: '12px',
              background: selectedPackage
                ? 'linear-gradient(135deg, #a855f7 0%, #8b5cf6 50%, #7c3aed 100%)'
                : 'linear-gradient(135deg, #d1d5db 0%, #9ca3af 100%)',
              border: 'none',
              boxShadow: selectedPackage
                ? '0 4px 16px rgba(139, 92, 246, 0.4), 0 2px 8px rgba(139, 92, 246, 0.3)'
                : 'none',
              color: '#ffffff',
              fontWeight: 600
            }}
          >
            {loading ? (
              <span style={{ fontSize: '16px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <PayCircleOutlined /> 支付中...
              </span>
            ) : selectedPackage
              ? (() => {
                  const list = (packages.length > 0 ? packages : defaultCreditPackages) as any[]
                  const price = list.find(p => p.id === selectedPackage)?.price
                  return (
                    <span style={{ fontSize: '16px', fontWeight: 600, color: '#ffffff', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <PayCircleOutlined style={{ fontSize: '18px' }} /> 立即支付 ￥{price ?? ''}
                    </span>
                  )
                })()
              : (
                <span style={{ fontSize: '16px', fontWeight: 600, color: '#6b7280', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <PayCircleOutlined style={{ fontSize: '18px' }} /> 请选择积分包
                </span>
              )}
          </Button>
        </div>,
        document.body
      )}
      
      {/* 聚合支付PC端二维码弹窗 */}
      <Modal
        title="微信扫码支付"
        open={qrCodeVisible}
        onCancel={() => {
          setQrCodeVisible(false)
          setPolling(false)
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current)
            pollingIntervalRef.current = null
          }
        }}
        footer={null}
        width={400}
        centered
        maskClosable={false}
      >
        <div style={{ textAlign: 'center', padding: '20px' }}>
          {qrCodeUrl ? (
            <>
              {/* 使用图片显示二维码（聚合支付返回的 qrCode 是图片 URL） */}
              <div style={{ marginBottom: '8px' }}>
                <Text strong>支付二维码</Text>
                    </div>
              <img 
                src={qrCodeUrl} 
                alt="支付二维码" 
                style={{ 
                  width: 256, 
                  height: 256, 
                  margin: '0 auto', 
                  display: 'block', 
                  border: '1px solid #ddd', 
                  borderRadius: '8px',
                  objectFit: 'contain'
                }}
                onError={(e) => {
                  console.error('❌ [PaymentPage] 二维码图片加载失败:', qrCodeUrl)
                  message.error('二维码加载失败，请刷新页面重试')
                }}
                onLoad={() => {
                  console.log('✅ [PaymentPage] 二维码图片加载成功:', qrCodeUrl)
                }}
              />
              <div style={{ marginTop: '20px' }}>
                <Text type="secondary">请使用手机微信扫描二维码完成支付</Text>
                    </div>
              {polling ? (
                <div style={{ marginTop: '10px' }}>
                  <Spin size="small" /> <Text type="secondary">等待支付完成...</Text>
                </div>
              ) : (
                <div style={{ marginTop: '15px' }}>
                  <Button 
                    type="primary" 
                    onClick={() => {
                      if (currentOrderId) {
                        manualCheckPaymentStatus(currentOrderId)
                      } else {
                        message.warning('无法获取订单信息，请刷新页面重试')
                      }
                    }}
                    loading={polling}
                    style={{ marginRight: '10px' }}
                  >
                    手动检查支付状态
                  </Button>
                  <Button 
                    onClick={() => {
                      setQrCodeVisible(false)
                      setPolling(false)
                      if (pollingIntervalRef.current) {
                        clearInterval(pollingIntervalRef.current)
                        pollingIntervalRef.current = null
                      }
                    }}
                  >
                    关闭
                  </Button>
                </div>
              )}
            </>
          ) : (
            <Spin size="large" />
          )}
        </div>
      </Modal>
    </div>
  )
}


export default PaymentPage

